// Content gates for sidebar nav items.
//
// Why this exists: the sidebar's primary filter is the page-access matrix
// (`hasAccess`), but several feature views apply their OWN, stricter permission
// check and render "Bạn không có quyền xem" when it fails. That made the menu
// show pages the user couldn't actually view (QA audit P2 / Lane C smoke:
// /rankings, /quality, /salary-analytics, /reports, /customers showed for roles
// the view then blocked).
//
// This map mirrors each gated view's guard so the sidebar hides the item when
// the content would be blocked. Source of truth stays the view helpers — this
// just calls them, so the two can't drift. Pages absent from the map have no
// content gate (menu shows whenever the matrix allows).

import { canReadHalfDaySlots } from "@/features/half-day-slots/permissions";
import type { Staff } from "@/lib/authv2/store";
import { canReadQuality } from "@/features/quality/permissions";
import { canReadSalaryAnalytics } from "@/features/salary-analytics/permissions";
import { canReadReports } from "@/features/reports/permissions";
import { canSeeAllCustomers } from "@/features/customers/permissions";
import { visibleCskhTabs } from "@/features/cskh/tabAccess";
import { canReadObligations } from "@/features/obligations/permissions";
import { canReadAiBriefing } from "@/features/ai-briefing/permissions";
import { canReadIdeaBank } from "@/features/idea-bank/permissions";
import { canReadOverdueLoans } from "@/features/equipment-loans/permissions";
import { showKhoInMenu, showLoanScannerInMenu } from "@/features/kho/permissions";
import { showStaffChannelInMenu } from "@/features/staff-channel/permissions";
import { canReadMyNotifications } from "@/features/notifications/permissions";
import { canViewKpi } from "@/features/branch-kpi/permissions";
import {
  canOpenFrameworkDesk,
  canOpenPerformancePage,
  canOpenReviewDesk,
  canReadFraudSignals,
} from "@/features/performance/permissions";
import { canReadBranchShifts } from "@/features/branch-shift/permissions";
import {
  canConfirmCashSession,
  canOperateCashDrawer,
  canReadCashChain,
  canRemitCash,
} from "@/features/cashdrawer/permissions";
import { canSeeStoreBoard } from "@/features/store-board/permissions";
import { canSeeSetBoard } from "@/features/set-board/permissions";
import { canManageCustomerChats } from "@/features/customer-chat/permissions";
import { canChatAffiliates } from "@/features/affiliates/permissions";
import { canManagePrinterInvoices } from "@/features/printer-invoices/permissions";
import { canReadPrintOpsReport } from "@/features/print-ops-report/permissions";
import { canReadEkipShifts } from "@/features/ekip-shift/permissions";
import { canViewQcBoard } from "@/features/qc-board/permissions";
import { canReadPhotoWork } from "@/features/photo-work/permissions";
import {
  canSeeDispatchBoard,
  canSeeDispatchPoints,
  canSeeDispatchSettings,
  canSeeMyDispatch,
} from "@/features/order-dispatch/permissions";
import { canAuditLooseAlbums, canManageGallerySettings, canOpenAlbumManager } from "@/features/gallery/permissions";
import { canManageMakeupTierGate } from "@/features/makeup-tier-gate/permissions";
import { canReadPromos } from "@/features/promos/permissions";
import { canReadPrintOps } from "@/features/print-ops/permissions";
import { canConfigureRetouchDispatch } from "@/features/retouch-dispatch/permissions";
import { canReadRuleV2 } from "@/features/salary-rules-v2/permissions";
import { canReadExpenses } from "@/features/expenses/permissions";
import { canSeeFraudBoard } from "@/features/fraud-board/permissions";
import { canSeeStaffProductivity } from "@/features/staff-productivity/permissions";
import { canViewBranchScores } from "@/features/branch-score/permissions";
import { canSeeMgmtPerformance } from "@/features/mgmt-performance/permissions";
import { canSeeSalesTeam } from "@/features/sales-team/permissions";
import { canReadSealed, canSendSealed } from "@/features/sealed-inbox/permissions";
import { canReadBackdrops } from "@/features/compose-backdrops/permissions";

import { ROLE } from "@/lib/auth/roles";
import { qcKindOf } from "@/lib/auth/role-labels";

type Gate = (staff: Staff) => boolean;

const GATES: Record<string, Gate> = {
  // "rankings" cố ý KHÔNG có gate: bảng xếp hạng & huy chương mở cho mọi nhân
  // sự (CEO chốt 20/08/2026). Trước đây gate bằng payroll.read khiến nhân viên
  // thường không thấy menu, dù ma trận PAGE_ACCESS đã cho phép họ vào trang.
  quality: (s) => canReadQuality(s),
  // Mirror guard của QcBoardView: ma trận PAGE_ACCESS mở theo vai (admin,
  // director, qc), còn trang đòi thêm quyền review.read từ JWT. Không có gate
  // này thì vai được cấp trang nhưng thiếu quyền vẫn thấy menu rồi bấm vào
  // một trang báo không có quyền.
  qc_board: (s) => canViewQcBoard(s),
  // Mirror guard của BranchScoreView (QĐ 0246): trang đòi quyền
  // performance.branchscore.read từ JWT.
  branch_scores: (s) => canViewBranchScores(s),
  // Hiệu suất quản lý: cùng cổng với bốn cửa máy chủ `mgmtperf.read`.
  mgmt_performance: () => canSeeMgmtPerformance(),
  // Phòng Kinh doanh: cùng cổng với cửa máy chủ `sales.structure.read`.
  sales_team: () => canSeeSalesTeam(),
  // Bảng QC: cùng cổng với cửa máy chủ `performance.fraud.read`.
  qc_overview: (s) => canReadFraudSignals(s),
  // Bàn làm việc QC (QĐ 0521): chỉ vai qc.
  qc_desk: (s) => (s.role ?? "").toLowerCase().trim() === ROLE.QC,
  // Mục "Hậu kiểm ảnh gốc" (QĐ 0521, CEO 2a): chỉ QC sửa hình.
  qc_raw_review: (s) => (s.role ?? "").toLowerCase().trim() === ROLE.QC && ["retouch", "photo"].includes(qcKindOf(s.department)),
  qc_journey: (s) => (s.role ?? "").toLowerCase().trim() === ROLE.QC && qcKindOf(s.department) === "cskh",
  qc_idphoto: (s) => (s.role ?? "").toLowerCase().trim() === ROLE.QC && qcKindOf(s.department) === "photo",
  salary_rules_v2: (s) => canReadRuleV2(s),
  salary_analytics: (s) => canReadSalaryAnalytics(s),
  reports: (s) => canReadReports(s),
  customers: (s) => canSeeAllCustomers({ id: s.id, role: s.role, department: s.department }),
  store_board: (s) => canSeeStoreBoard(s),
  set_board: (s) => canSeeSetBoard(s),
  // Mục "Booking" mở /cskh?tab=booking: menu hiện đúng khi trang cho thấy tab
  // đó. Cổng cũ theo nhận diện mở cho MỌI trưởng phòng, trong khi tab Booking
  // chỉ dành cho người CSKH + điều hành (tabAccess.ts) — bảy trưởng phòng bấm
  // vào ra "Bạn không có quyền xem nội dung nào" (kiểm thử toàn vai 16/09/2026).
  cskh_booking: (s) => visibleCskhTabs(s).some((t) => t.key === "booking"),
  // Mirror guard của ObligationsView (`obligation.read`): ma trận trang mở cho
  // kế toán trưởng nhưng máy chủ không cấp quyền đọc sổ cho vai manager.
  obligations: (s) => canReadObligations(s),
  // Mirror guard của OverdueLoansView (`equipment_loan.overdue.read`): Casdoor
  // chưa đồng bộ quyền thì menu không hiện thay vì dẫn vào trang báo cấm.
  equipment_overdue: (s) => canReadOverdueLoans(s),
  ai_briefing: (s) => canReadAiBriefing(s),
  // Kho Ý Tưởng (QĐ 0445): chỉ CEO + giám đốc có `idea_bank.read`.
  idea_bank: (s) => canReadIdeaBank(s),
  // Mục Kho: quyền `kho.read` VÀ công tắc KHO_ERP_ENABLED (mặc định tắt).
  kho: (s) => showKhoInMenu(s),
  // "Mượn / trả đồ": máy quét trong ERP — quyền gửi lệnh kho VÀ hai công tắc.
  muon_tra_do: (s) => showLoanScannerInMenu(s),
  // Kênh nhân sự: máy chủ bật luồng kenh_nhan_su cho chính người này.
  staff_channel: () => showStaffChannelInMenu(),
  // Thông báo của tôi: cùng quyền với chuông.
  my_notifications: () => canReadMyNotifications(),
  // Hộp thư kín (QĐ 0272): quyền trong phiên VÀ máy chủ đã bật mục; mục đọc còn
  // đòi có tên trong danh sách người đọc — quyền thô thôi là chưa đủ (Câu 2a).
  sealed_inbox: () => canSendSealed(),
  sealed_inbox_reader: () => canReadSealed(),
  branch_kpi: (s) => canViewKpi(s),
  // "Hiệu suất của tôi": cổng hỏi CẢ BA quyền, không chỉ `read.self`. Cửa này
  // còn thẻ CÔNG KHAI "Sổ quỹ chung" (`show: true`), nên người thiếu đúng
  // `read.self` — thường vì Casdoor chưa gán vai, không phải vì bị cấm — vẫn
  // phải vào được. Xem `canOpenPerformancePage`.
  performance: (s) => canOpenPerformancePage(s),
  // Hai cửa kia thì KHÔNG. Ma trận trang mở cả ba cùng lúc (page-access sinh
  // ra thế), nên đây là chỗ DUY NHẤT cắt lại: không có việc chấm ai thì không
  // thấy mục "Chấm điểm", không quản khung thì không thấy "Điều hành khung".
  // Thiếu hai dòng này là mọi nhân viên thấy hai mục rồi bấm vào trang trắng.
  performance_review: (s) => canOpenReviewDesk(s),
  performance_admin: (s) => canOpenFrameworkDesk(s),
  // Ca vận hành: gate theo permission JWT branchshift.read — role không có
  // quyền đó (VD viewer) không thấy menu dù ma trận PAGE_ACCESS cho vào trang.
  branch_shifts: (s) => canReadBranchShifts(s),
  half_day_slots: (s) => canReadHalfDaySlots(s),
  // Mirrors CashDrawerView's own guard: lễ tân giữ két, kế toán nộp tiền.
  // Ai không thuộc hai nhóm đó thấy menu là bấm vào một trang trắng.
  // Người KÝ két cũng phải tới được két (rà soát 10/10/2026): quản lý cụm và
  // trưởng cửa hàng vùng có quyền ký mà trước đó menu không dẫn tới đâu.
  cash_drawer: (s) => canOperateCashDrawer(s) || canRemitCash(s) || canConfirmCashSession(s),
  cash_chain: (s) => canReadCashChain(s),
  // Hai cửa vào cùng một trang: admin quản toàn bộ chat khách, hoặc có quyền
  // affiliate.chat để đọc mục "Người giới thiệu & đối tác" (spec 22/08/2026 §9.4).
  customer_chats: (s) => canManageCustomerChats(s) || canChatAffiliates(s),
  // Mirror guard của EkipShiftsView: ma trận PAGE_ACCESS mở theo role, còn
  // trang chỉ nhận khối photo/makeup + điều phối. Không có gate này thì staff
  // retouch/CSKH thấy menu rồi bấm vào một trang báo không có quyền.
  ekip_shifts: (s) => canReadEkipShifts(s),
  // Mirror guard của PhotoWorkPage: ma trận mở theo role×phòng, trang chỉ nhận
  // khối ảnh hoặc người có photo.retouch.read — thiếu gate là điều phối không
  // có permission thấy menu rồi bấm vào trang báo không có quyền.
  photo_work: (s) => canReadPhotoWork(s),
  // Mirror cổng tuyến GET /gallery/albums (QĐ 0301): ai tới được cửa xem cây
  // album thì mở được trang; sale qua bằng gallery.compose.write. Thiếu gate là
  // người có trong ma trận nhưng vé chưa mang quyền (sale khi Casdoor chưa đồng
  // bộ) thấy menu rồi bấm vào một trang báo không có quyền.
  album_manager: (s) => canOpenAlbumManager(s),
  // Sổ album ngoài đơn: ma trận mở cho quản trị + giám đốc; cổng cắt theo quyền
  // thật trên vé (gallery.loose.audit) — vé chưa đồng bộ thì không hiện menu.
  album_ledger: (s) => canAuditLooseAlbums(s),
  // Kho ảnh nền (QĐ 0353): ma trận mở theo vai; cổng cắt theo cùng luật máy chủ
  // (compose.backdrop.read hoặc phòng hậu kỳ / CSKH / marketing).
  compose_backdrops: (s) => canReadBackdrops(s),
  // "Suất của tôi": ma trận mở theo khoá phòng, nhưng khoá `qc_photo` /
  // `qc_makeup` được SINH TỰ ĐỘNG từ `manager_photo` / `manager_makeup`
  // (`qcPagesOf`) nên QC khối ảnh thừa kế trang này mà không ai gõ tay. QC
  // soi chất lượng, không bao giờ được máy xếp vào suất chụp — cổng này là
  // chỗ DUY NHẤT cắt lại. Bỏ nó đi là QC/quản lý cụm thấy một trang rỗng.
  my_dispatch: (s) => canSeeMyDispatch(s),
  // "Bảng suất cơ sở": cổng backend là `AllowOrDept("dispatch.read",
  // "reception")` — lễ tân mang role `staff` nên KHÔNG có quyền trong JWT, chỉ
  // qua được bằng phòng ban. Ma trận trang không biểu đạt nổi cái "hoặc" đó
  // (khoá `staff_reception` mở trang cho mọi lễ tân, kể cả khi Casdoor chưa
  // đồng bộ quyền), nên gate này là bản sao duy nhất của cổng thật.
  dispatch_board: (s) => canSeeDispatchBoard(s),
  dispatch_points: () => canSeeDispatchPoints(),
  // Chống gian lận: ma trận trang mở cho quản trị + giám đốc, nhưng quyền thật
  // nằm trong JWT (fraud.read). Casdoor chưa đồng bộ kịp là token chưa mang
  // quyền — thiếu gate này thì menu vẫn hiện rồi bấm vào trang báo 403.
  fraud_board: () => canSeeFraudBoard(),
  // Năng suất nhân sự (QĐ 0386): ma trận mở cho lead / QC / trưởng cửa hàng /
  // điều hành, quyền thật là staff_productivity.read trong vé — vé chưa đồng
  // bộ thì menu ẩn thay vì mở ra trang báo không có quyền.
  staff_productivity: () => canSeeStaffProductivity(),
  // Ưu đãi: ma trận mở cho trưởng Sale/Marketing, nhưng quyền thật nằm trong
  // JWT (promo.read / promo.write). Casdoor chưa đồng bộ kịp là token chưa
  // mang quyền — thiếu gate này thì menu vẫn hiện rồi bấm vào trang báo "Bạn
  // không có quyền xem".
  promo: (s) => canReadPromos(s),
  // Cài đặt máy chia đơn: ma trận trang mở cho quản trị / giám đốc / trưởng
  // cửa hàng, nhưng quyền thật nằm trong JWT và CHIA BA (config.write,
  // designee.write, ordertag.manage). Trưởng cửa hàng chỉ có cái giữa —
  // cổng này để menu không hiện khi Casdoor chưa đồng bộ quyền nào cả.
  dispatch_settings: () => canSeeDispatchSettings(),
  // Cài đặt chung: admin có thẻ Gallery (gallery.settings.write + vai admin);
  // giám đốc chỉ có thẻ "Bậc thợ trang điểm" (settings.write). Có MỘT trong
  // hai là có việc ở trang.
  general_settings: (s) => canManageGallerySettings(s) || canManageMakeupTierGate(),
  // Sổ chi phí: ma trận trang mở cho kế toán / trưởng cửa hàng, nhưng từ
  // 13/09/2026 (QĐ 0132 §8 câu 4a) máy chủ gác cả ĐỌC bằng `expense.read`.
  // Vé chưa đăng nhập lại sau `casdoor-policy-sync` chưa mang quyền — thiếu
  // gate là menu hiện rồi bấm vào một trang báo không có quyền.
  expenses: (s) => canReadExpenses(s),
  print_ops: (s) => canReadPrintOps(s),
  // Đối soát nhà in (gói I màn 8): ma trận mở cho quản trị / giám đốc / kế
  // toán; quyền thật là print.printer_invoice.manage trong vé — chưa đồng bộ
  // thì menu ẩn thay vì mở ra trang báo không có quyền.
  printer_invoices: (s) => canManagePrinterInvoices(s),
  // Vận hành in (gói I màn 9): ma trận mở cho quản trị / giám đốc / trưởng cửa
  // hàng; quyền thật là print.ops.report trong vé.
  print_ops_report: (s) => canReadPrintOpsReport(s),
  // Máy chia việc sửa hình: ma trận mở cho trưởng phòng hậu kỳ + QC hậu kỳ
  // (thừa kế), nhưng menu chỉ hiện cho người SỬA được (retouch.dispatch.
  // config.write + đúng phòng). QC/trưởng cửa hàng vẫn vào được bằng đường
  // dẫn để xem — trang tự gác bằng canReadRetouchDispatch.
  retouch_dispatch_settings: (s) => canConfigureRetouchDispatch(s),
};

/**
 * True when the nav item with `id` should be shown to `staff` given its
 * content gate. Items with no gate always pass.
 */
export function passesContentGate(id: string, staff: Staff): boolean {
  const gate = GATES[id];
  return gate ? gate(staff) : true;
}
